import {
  SuggestionsApi,
  SuggestionResponse,
  SuggestionListResponse,
  CreateSuggestionRequest,
  UpdateSuggestionRequest,
  ReviewSuggestionRequest,
  AttachSuggestionCitationsRequest,
  SuggestionStatus,
  SuggestionTargetType,
  SuggestionSortBy,
  ListSuggestionsSortOrderEnum,
  SuggestionCitationSummary
} from 'we-tree-api-client/v2';

import { getApiConfiguration } from '@/services/SessionService';
import { ApiResult, withApiSetup } from './apiWrapper';

export interface ListSuggestionsOptions {
  status?: SuggestionStatus;
  targetType?: SuggestionTargetType;
  targetId?: string;
  createdBy?: string;
  sortBy?: SuggestionSortBy;
  sortOrder?: ListSuggestionsSortOrderEnum;
  page?: number;
  pageSize?: number;
}

class WrappedSuggestionsApi {
  async listSuggestions(
    familyId: string,
    options: ListSuggestionsOptions = {}
  ): Promise<ApiResult<SuggestionListResponse>> {
    return withApiSetup(
      (config) => new SuggestionsApi(config),
      getApiConfiguration,
      (api) =>
        api.listSuggestions(
          familyId,
          options.status,
          options.targetType,
          options.targetId,
          options.createdBy,
          options.sortBy,
          options.sortOrder,
          options.page,
          options.pageSize
        )
    );
  }

  async getSuggestion(familyId: string, suggestionId: string): Promise<ApiResult<SuggestionResponse>> {
    return withApiSetup(
      (config) => new SuggestionsApi(config),
      getApiConfiguration,
      (api) => api.getSuggestion(familyId, suggestionId)
    );
  }

  async createSuggestion(familyId: string, request: CreateSuggestionRequest): Promise<ApiResult<SuggestionResponse>> {
    return withApiSetup(
      (config) => new SuggestionsApi(config),
      getApiConfiguration,
      (api) => api.createSuggestion(familyId, request)
    );
  }

  async updateSuggestion(
    familyId: string,
    suggestionId: string,
    request: UpdateSuggestionRequest
  ): Promise<ApiResult<SuggestionResponse>> {
    return withApiSetup(
      (config) => new SuggestionsApi(config),
      getApiConfiguration,
      (api) => api.updateSuggestion(familyId, suggestionId, request)
    );
  }

  async cancelSuggestion(familyId: string, suggestionId: string): Promise<ApiResult<SuggestionResponse>> {
    return withApiSetup(
      (config) => new SuggestionsApi(config),
      getApiConfiguration,
      (api) => api.cancelSuggestion(familyId, suggestionId)
    );
  }

  async approveSuggestion(
    familyId: string,
    suggestionId: string,
    request?: ReviewSuggestionRequest
  ): Promise<ApiResult<SuggestionResponse>> {
    return withApiSetup(
      (config) => new SuggestionsApi(config),
      getApiConfiguration,
      (api) => api.approveSuggestion(familyId, suggestionId, request)
    );
  }

  async rejectSuggestion(
    familyId: string,
    suggestionId: string,
    request?: ReviewSuggestionRequest
  ): Promise<ApiResult<SuggestionResponse>> {
    return withApiSetup(
      (config) => new SuggestionsApi(config),
      getApiConfiguration,
      (api) => api.rejectSuggestion(familyId, suggestionId, request)
    );
  }

  async requestSuggestionChanges(
    familyId: string,
    suggestionId: string,
    request?: ReviewSuggestionRequest
  ): Promise<ApiResult<SuggestionResponse>> {
    return withApiSetup(
      (config) => new SuggestionsApi(config),
      getApiConfiguration,
      (api) => api.requestSuggestionChanges(familyId, suggestionId, request)
    );
  }

  async attachSuggestionCitations(
    familyId: string,
    suggestionId: string,
    request: AttachSuggestionCitationsRequest
  ): Promise<ApiResult<SuggestionCitationSummary[]>> {
    return withApiSetup(
      (config) => new SuggestionsApi(config),
      getApiConfiguration,
      (api) => api.attachSuggestionCitations(familyId, suggestionId, request)
    );
  }
}

export const suggestionsApi = new WrappedSuggestionsApi();

export default suggestionsApi;
